import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, CheckCheck, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useApi } from "@/hooks/useApi";
import { PageHeader, LoadingGrid, ErrorState, EmptyState, Pager } from "@/components/common";
import { useBranding } from "@/context/BrandingContext";
import { api, fileUrl } from "@/lib/api";
import { ftime } from "@/lib/format";
import { cn } from "@/lib/utils";

export function NotificationsList({ embedded }) {
  const nav = useNavigate();
  const { branding } = useBranding();
  const [page, setPage] = useState(1);
  const { data, loading, error, reload } = useApi("/notifications", { page });
  const open = async (n) => {
    if (!n.read) await api.post(`/notifications/${n.id}/read`).catch(() => {});
    if (n.link && n.link !== "/notifications" && n.link !== "/admin/notifications") nav(n.link); else reload();
  };
  const readAll = async () => { await api.post("/notifications/read-all"); reload(); };
  return (
    <div>
      {!embedded && (
        <PageHeader eyebrow="Inbox" title="Notifications">
          <Button variant="outline" className="rounded-full" onClick={readAll} data-testid="notifications-mark-all-read"><CheckCheck className="h-4 w-4 mr-2" />Mark all as read</Button>
        </PageHeader>
      )}
      {loading ? <LoadingGrid count={4} className="space-y-3" h="h-20" /> : error ? <ErrorState message={error} onRetry={reload} /> :
        data.items.length === 0 ? <EmptyState icon={Bell} title="You're all caught up." testid="notifications-empty" /> : (
          <div className="space-y-2.5 stagger">
            {data.items.map((n) => (
              <div key={n.id} role="button" onClick={() => open(n)} data-testid={`notification-row-${n.id}`}
                className={cn("uc-card uc-card-hover p-4 sm:p-5 cursor-pointer flex gap-4", !n.read && "border-primary/30")}>
                <span className={cn("mt-2 h-2 w-2 rounded-full shrink-0", n.read ? "bg-border" : "gold-bg")} />
                <div className="min-w-0 flex-1">
                  {n.type === "broadcast" && <p className="eyebrow text-primary mb-1">{branding.website_name}</p>}
                  <p className="font-medium">{n.title}</p>
                  <p className="text-sm text-muted-foreground whitespace-pre-wrap mt-0.5">{n.message}</p>
                  {n.image && <img src={fileUrl(n.image)} alt="" className="mt-3 rounded-xl max-h-64 object-cover" />}
                  {n.ext_link && <a href={n.ext_link} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="text-xs text-primary mt-2 inline-flex items-center gap-1 hover:underline"><ExternalLink className="h-3 w-3" />Open link</a>}
                  <p className="text-[11px] text-muted-foreground/70 mt-2">{ftime(n.created_at)}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      {data && <Pager page={page} total={data.total} onPage={setPage} />}
    </div>
  );
}

export default function NotificationsPage() {
  return <div className="max-w-3xl"><NotificationsList /></div>;
}
